// Removing one-shot flags from the URL once they have been acted on.
//
// ⚠ A FLAG THAT SURVIVES ITS OWN NOTICE IS A LIE THE SECOND TIME IT IS READ.
// `?connected=1`, `?connectionError=…` and `?checkout=success` all drive UI and
// none of them were cleared, so refreshing re-showed "just connected" or
// re-entered the post-checkout poll, and a bookmarked or shared link claimed a
// thing had just happened whenever it was opened.
//
// The `?signup=1` path already got this right — it strips before redirecting to
// Entra, precisely so that cancelling sign-up cannot return the customer to a
// URL that immediately sends them back. Same idea, applied to the flags that
// report an outcome instead of requesting one.
//
// ⚠ STRIP WITH `replace`, NEVER a push. Adding a history entry would mean the
// back button returns to the flagged URL and re-fires the notice, which is the
// loop this exists to avoid.

/**
 * Whether any of `keys` is present, so callers can skip a pointless history
 * write on every render.
 */
export function hasAnyQueryKey(params: URLSearchParams, keys: readonly string[]): boolean {
  return keys.some((key) => params.has(key));
}

/**
 * A copy of `params` with `keys` removed.
 *
 * Returns a new object rather than mutating: the caller usually still needs to
 * read the flag it is about to strip, and a mutated `URLSearchParams` from
 * useSearchParams makes that order matter in a way nobody will remember.
 */
export function stripQueryKeys(
  params: URLSearchParams,
  keys: readonly string[]
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of keys) next.delete(key);
  return next;
}
